import {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DOMUtils from '@livongo/utils/dom';
import Button from 'ui/Button';
import Portal from 'ui/Portal';
import {withTranslation} from 'react-i18next';

class Flyout extends Component {
    static propTypes = {
        /** Additional class(es) to add to the component. */
        className: PropTypes.string,
        /** The content to insert into the flyout. */
        children: PropTypes.node.isRequired,
        /** The title to include at the top of the flyout. */
        title: PropTypes.node.isRequired,
        /**
         * A function to execute after the flyout has been hidden. Use this to manage state in a parent component to remove the flyout from the DOM (see examples).
         *
         * @param {*} args - Any arguments that were passed to `hide()` to retrieve in the callback.
         */
        onHidden: PropTypes.func,
        t: PropTypes.func.isRequired,
    };

    componentDidMount() {
        setTimeout(() => {
            this._isAnimating = true;

            DOMUtils.addClass('html', 'Flyout-open');
            DOMUtils.addClass(this._flyout, 'Flyout-active');

            window.transitionEnd(this._flyout).bind(() => {
                window.transitionEnd(this._flyout).unbind();

                this._isAnimating = false;

                this._flyout.focus();
            });
        }, 100);
    }

    _onCloseClick = evt => {
        if (this._isAnimating) {
            return;
        }

        this.hide();
    };

    /**
     * Hides the Flyout programmatically.
     *
     * @public
     * @param {*} args - The arguments to pass to the onHidden callback in cases where you may need access to something not available in Flyout itself.
     * @returns {void}
     */
    hide(...args) {
        this._isAnimating = true;

        const {onHidden} = this.props;

        DOMUtils.removeClass('html', 'Flyout-open');
        DOMUtils.removeClass(this._flyout, 'Flyout-active');

        window.transitionEnd(this._flyout).bind(() => {
            window.transitionEnd(this._flyout).unbind();

            this._isAnimating = false;

            if (onHidden) {
                onHidden(...args);
            }
        });
    }

    render() {
        const {t, className, title, children} = this.props;
        const classes = classNames('Flyout-container', className);

        return (
            <Portal className="Flyout">
                <div className={classes}>
                    <div className="Flyout-shim" onClick={this._onCloseClick} />
                    <div
                        ref={node => {
                            this._flyout = node;
                        }}
                        tabIndex="-1"
                        className="Flyout-wrapper"
                    >
                        <div className="Flyout-header">
                            <div className="Flyout-constraint">
                                <p>{title}</p>
                                <Button onClick={this._onCloseClick}>
                                    {t('close')}
                                </Button>
                            </div>
                        </div>
                        <div className="Flyout-content">
                            <div className="Flyout-constraint">{children}</div>
                        </div>
                    </div>
                </div>
            </Portal>
        );
    }
}

export default withTranslation('remonj', {withRef: true})(Flyout);
